import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { CashFlowAllocationState, CashFlowAllocation, AllocationStrategy, AllocationHistoryEntry } from '@/types'

const initialState: CashFlowAllocationState = {
  monthlyFreeCashFlow: 0,
  allocation: {
    investments: 0,
    cash: 0,
    other: 0,
  },
  targets: {
    investmentTarget: 0,
    cashTarget: 0,
  },
  history: [],
  autoAllocate: false,
  allocationStrategy: 'balanced',
  annualContributions: {
    investments: 0,
    cash: 0,
    other: 0,
  },
}

const cashFlowAllocationSlice = createSlice({
  name: 'cashFlowAllocation',
  initialState,
  reducers: {
    updateFreeCashFlow: (state, action: PayloadAction<number>) => {
      state.monthlyFreeCashFlow = action.payload
      if (state.autoAllocate) {
        cashFlowAllocationSlice.caseReducers.autoAllocate(state)
      }
      cashFlowAllocationSlice.caseReducers.updateAnnualContributions(state)
    },
    updateAllocation: (state, action: PayloadAction<{ category: keyof CashFlowAllocation; amount: number }>) => {
      const { category, amount } = action.payload
      if (category in state.allocation) {
        const newPercentage = Math.max(0, Math.min(100, amount))
        state.allocation[category] = newPercentage
        cashFlowAllocationSlice.caseReducers.updateAnnualContributions(state)
      }
    },
    updateTarget: (state, action: PayloadAction<{ category: keyof typeof initialState.targets; amount: number }>) => {
      const { category, amount } = action.payload
      if (category in state.targets) {
        state.targets[category] = Math.max(0, amount)
      }
    },
    setAutoAllocate: (state, action: PayloadAction<boolean>) => {
      state.autoAllocate = action.payload
      if (action.payload) {
        cashFlowAllocationSlice.caseReducers.autoAllocate(state)
      }
    },
    setAllocationStrategy: (state, action: PayloadAction<AllocationStrategy>) => {
      state.allocationStrategy = action.payload
      if (state.autoAllocate) {
        cashFlowAllocationSlice.caseReducers.autoAllocate(state)
      }
    },
    autoAllocate: (state) => {
      const totalCashFlow = state.monthlyFreeCashFlow

      if (totalCashFlow <= 0) {
        state.allocation = {
          investments: 0,
          cash: 0,
          other: 0,
        }
        cashFlowAllocationSlice.caseReducers.updateAnnualContributions(state)
        return
      }

      let allocation = { investments: 0, cash: 0, other: 0 }

      switch (state.allocationStrategy) {
        case 'aggressive':
          allocation.investments = 85
          allocation.cash = 15
          break
        case 'conservative':
          allocation.investments = 50
          allocation.cash = 50
          break
        case 'balanced':
        default:
          allocation.investments = 70
          allocation.cash = 30
          break
      }

      state.allocation = allocation
      cashFlowAllocationSlice.caseReducers.updateAnnualContributions(state)
    },
    updateAnnualContributions: (state) => {
      const annualFreeCashFlow = state.monthlyFreeCashFlow
      state.annualContributions = {
        investments: (annualFreeCashFlow * state.allocation.investments) / 100,
        cash: (annualFreeCashFlow * state.allocation.cash) / 100,
        other: (annualFreeCashFlow * state.allocation.other) / 100,
      }
    },
    addAllocationHistory: (state, action: PayloadAction<Omit<AllocationHistoryEntry, 'id' | 'date'>>) => {
      const entry: AllocationHistoryEntry = {
        id: Date.now(),
        date: new Date().toISOString(),
        ...action.payload,
      }
      state.history.unshift(entry)
      if (state.history.length > 50) {
        state.history = state.history.slice(0, 50)
      }
    },
    setCashFlowAllocationData: (state, action: PayloadAction<Partial<CashFlowAllocationState>>) => {
      const data = action.payload
      if (data.monthlyFreeCashFlow !== undefined) state.monthlyFreeCashFlow = data.monthlyFreeCashFlow
      if (data.allocation) state.allocation = { ...state.allocation, ...data.allocation }
      if (data.targets) state.targets = { ...state.targets, ...data.targets }
      if (data.history) state.history = data.history
      if (data.autoAllocate !== undefined) state.autoAllocate = data.autoAllocate
      if (data.allocationStrategy) state.allocationStrategy = data.allocationStrategy
      if (data.annualContributions) state.annualContributions = { ...state.annualContributions, ...data.annualContributions }
      cashFlowAllocationSlice.caseReducers.updateAnnualContributions(state)
    },
    resetAllocation: (state) => {
      state.allocation = {
        investments: 0,
        cash: 0,
        other: 0,
      }
      cashFlowAllocationSlice.caseReducers.updateAnnualContributions(state)
    },
  },
})

export const {
  updateFreeCashFlow,
  updateAllocation,
  updateTarget,
  setAutoAllocate,
  setAllocationStrategy,
  autoAllocate,
  addAllocationHistory,
  setCashFlowAllocationData,
  resetAllocation,
} = cashFlowAllocationSlice.actions

export default cashFlowAllocationSlice.reducer
